import React, { useState } from 'react';
import { FileText, Plus, Edit2, Trash2 } from 'lucide-react';
import { seoService } from '../../../services/seoService';

const PageOptimizationPanel = ({ pages, onRefresh, fullView = false }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [formData, setFormData] = useState({
    pageUrl: '',
    pageTitle: '',
    metaDescription: '',
    h1Tag: '',
    targetKeywords: [],
    seoScore: 0,
    recommendations: []
  });

  const handleSubmit = async (e) => {
    e?.preventDefault();
    try {
      if (editingPage) {
        await seoService?.updatePageOptimization(editingPage?.id, formData);
      } else {
        await seoService?.createPageOptimization(formData);
      }
      setShowAddModal(false);
      setEditingPage(null);
      resetForm();
      onRefresh?.();
    } catch (error) {
      console.error('Error saving page optimization:', error);
      alert('Erreur lors de l\'enregistrement de la page');
    }
  };

  const handleEdit = (page) => {
    setEditingPage(page);
    setFormData({
      pageUrl: page?.page_url,
      pageTitle: page?.page_title,
      metaDescription: page?.meta_description,
      h1Tag: page?.h1_tag,
      targetKeywords: page?.target_keywords || [],
      seoScore: page?.seo_score,
      recommendations: page?.recommendations || []
    });
    setShowAddModal(true);
  };

  const handleDelete = async (pageId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer cette page ?')) return;
    try {
      await seoService?.deletePageOptimization(pageId);
      onRefresh?.();
    } catch (error) {
      console.error('Error deleting page:', error);
      alert('Erreur lors de la suppression de la page');
    }
  };

  const resetForm = () => {
    setFormData({
      pageUrl: '',
      pageTitle: '',
      metaDescription: '',
      h1Tag: '',
      targetKeywords: [],
      seoScore: 0,
      recommendations: []
    });
  };

  const displayPages = fullView ? pages : pages?.slice(0, 5);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <FileText className="w-6 h-6 text-green-600 mr-2" />
          <h3 className="text-lg font-semibold text-gray-900">Optimisation des pages</h3>
        </div>
        <button
          onClick={() => {
            resetForm();
            setEditingPage(null);
            setShowAddModal(true);
          }}
          className="flex items-center space-x-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
        >
          <Plus size={16} />
          <span>Ajouter</span>
        </button>
      </div>

      <div className="space-y-4">
        {displayPages?.map((page) => (
          <div key={page?.id} className="border border-gray-200 rounded-lg p-4 hover:border-green-300 transition-colors">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <h4 className="font-medium text-gray-900">{page?.page_title}</h4>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    page?.seo_score >= 80 ? 'bg-green-100 text-green-800' :
                    page?.seo_score >= 60 ? 'bg-yellow-100 text-yellow-800': 'bg-red-100 text-red-800'
                  }`}>
                    Score: {page?.seo_score}/100
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-1">{page?.page_url}</p>
                <p className="text-xs text-gray-500 line-clamp-2">{page?.meta_description}</p>
              </div>
              <div className="flex items-center space-x-2 ml-4">
                <button
                  onClick={() => handleEdit(page)}
                  className="p-1 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                >
                  <Edit2 size={16} />
                </button>
                <button
                  onClick={() => handleDelete(page?.id)}
                  className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
            {page?.target_keywords?.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {page?.target_keywords?.map((keyword, idx) => (
                  <span key={idx} className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full">
                    {keyword}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                {editingPage ? 'Modifier la page' : 'Ajouter une page'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL de la page *</label>
                  <input
                    type="text"
                    required
                    value={formData?.pageUrl}
                    onChange={(e) => setFormData({ ...formData, pageUrl: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                    placeholder="/shipping-information"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Titre de la page *</label>
                  <input
                    type="text"
                    required
                    value={formData?.pageTitle}
                    onChange={(e) => setFormData({ ...formData, pageTitle: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                    placeholder="ADT Express - Services d'envoi"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Meta Description</label>
                  <textarea
                    value={formData?.metaDescription}
                    onChange={(e) => setFormData({ ...formData, metaDescription: e?.target?.value })}
                    rows={3}
                    maxLength={160}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                    placeholder="Description optimisée pour les moteurs de recherche (max 160 caractères)"
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData?.metaDescription?.length || 0}/160 caractères</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Balise H1</label>
                  <input
                    type="text"
                    value={formData?.h1Tag}
                    onChange={(e) => setFormData({ ...formData, h1Tag: e?.target?.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                    placeholder="Titre principal de la page"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mots-clés cibles (séparés par des virgules)</label>
                  <input
                    type="text"
                    value={formData?.targetKeywords?.join(', ')}
                    onChange={(e) => setFormData({ ...formData, targetKeywords: e?.target?.value?.split(',')?.map(k => k?.trim())?.filter(k => k) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                    placeholder="envoi colis, shipping, transport maritime"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Score SEO (0-100)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={formData?.seoScore}
                    onChange={(e) => setFormData({ ...formData, seoScore: parseInt(e?.target?.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setEditingPage(null);
                      resetForm();
                    }}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    {editingPage ? 'Mettre à jour' : 'Ajouter'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PageOptimizationPanel;